import { AbstractControl, FormControl, ValidationErrors } from "@angular/forms";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { finnishSsnValidator } from "./ssn-finnish.validator";
import { isValidNorwegianSsn } from "./ssn-norwegian.validator";
import { isValidSwedishSsn } from "./ssn-swedish.validator";

type SsnCheck = (ssn: string) => boolean;

// Can't use existing langCode enum here, because country codes and lang codes are not the same thing.
const SSN_CHECKS_BY_COUNTRY: Record<string, SsnCheck> = {
  fi: (ssn) => finnishSsnValidator(new FormControl(ssn)) === null,
  se: isValidSwedishSsn, // "se" is the correct country code for Sweden. "sv" is the language code.
  no: isValidNorwegianSsn,
};

/**
 * Accepts an SSN from any of the countries in `options.countries` (fi, se, no). Usage in a form field:
 *
 * ```json
 * "validators": {
 *   "validation": [{ "name": "ssn", "options": { "countries": ["fi", "se", "no"] } }]
 * }
 * ```
 */
export function ssnValidator(
  control: AbstractControl,
  _field: FormlyFieldConfig,
  options?: Record<string, unknown>,
): ValidationErrors | null {
  const countrySsnChecks = parseCountrySsnChecksFromOptions(options);

  if (!control.value) {
    return null; // Don't validate empty values to allow optional fields
  }

  const ssn = control.value.toString().trim();
  return countrySsnChecks.some((isValidSsn) => isValidSsn(ssn)) ? null : { ssn: true };
}

function parseCountrySsnChecksFromOptions(options: Record<string, unknown> | undefined): SsnCheck[] {
  const countries = options?.["countries"];
  if (!Array.isArray(countries) || countries.length === 0) {
    throw new Error('The "ssn" validator needs a non-empty "countries" option, e.g. { "countries": ["fi"] }');
  }

  const countrySsnChecks: SsnCheck[] = [];
  for (const country of countries) {
    const isValidSsn =
      typeof country === "string" && Object.hasOwn(SSN_CHECKS_BY_COUNTRY, country)
        ? SSN_CHECKS_BY_COUNTRY[country]
        : undefined;
    if (!isValidSsn) {
      const supportedCountries = Object.keys(SSN_CHECKS_BY_COUNTRY).join(", ");
      throw new Error(
        `Unknown country ${JSON.stringify(country)} for the "ssn" validator, expected one of: ${supportedCountries}`,
      );
    }
    countrySsnChecks.push(isValidSsn);
  }
  return countrySsnChecks;
}
